import { useEffect, useRef, useState } from 'react';
import {
    BoundedContent,
    Box,
    Button,
    CarouselSlide,
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    H1,
    Link,
    ListPrice,
    Pricing,
    Row,
    SkeletonText,
    SkeletonTextLine,
    ThumbnailsHero,
    ThumbnailsHeroImagesSlidesContainer,
    ThumbnailsHeroThumbnailsSlidesContainer,
    Typography,
    tokens,
} from '@vp/swan';
import { PriceFromFloat } from '@99designs/design-services-common';
import { ServiceProductOptions } from '@99designs/feature-design-services-brief';
import { __, getRequestBriefCreateUrl } from '@99designs/i18n';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { buildSrcset } from '../../..';
import { ServiceDetailsPageDataQuery } from '../../../graphql';
import { ProductOptionsQuery } from '../../../graphql';
import { useProductOptionsPricing } from '../../../hooks/useProductOptionsPricing';
import { PrimaryCTAUrlBuilder } from '../../Header/PrimaryCTAUrlBuilder';
import { ProductOptions } from '../ProductOptions';

type Images = ServiceDetailsPageDataQuery['serviceDetailPage']['heroImages'];

export type HeaderProps = {
    heroImages: Images;
    name: string;
    sectionTitle: string;
    description: string;
    cta: ServiceDetailsPageDataQuery['serviceDetailPage']['primaryCta'];
    routeSpecifiedMpvId: string;
    printMpvId: string;
    workId: string;
    environment: string;
    servicePrd: string;
    serviceVersion: number;
    productOptions: ProductOptionsQuery['productOptions'];
    renderInclusions?: () => JSX.Element;
};

function clickEventData(ctaUrl: string) {
    const pageZone = 'Merchandising Section';
    const label = `Content:${pageZone}`;
    const ctaValue = `Get Started-CTA`;
    const source = window.location.pathname;
    const eventDetail = `${source};${ctaUrl};${pageZone};${ctaValue}`;

    return {
        label,
        eventDetail,
        navigationDetail: ctaValue,
    };
}

function getPreselectedOptions(options: ProductOptionsQuery['productOptions']) {
    const selectedOptions: Record<string, string> = {};
    options.forEach((option) => {
        option.values.forEach((value) => {
            if (value.isPreselected) selectedOptions[option.key.key] = value.key;
        });
    });
    return selectedOptions;
}

export function Header(props: HeaderProps) {
    const accessibleTextForDots = [''];
    const inclusions = props.renderInclusions || (() => null);
    const { pageData } = usePage();
    const defaultCtaUrl = new PrimaryCTAUrlBuilder(getRequestBriefCreateUrl())
        .withPrintMpvId(props.printMpvId)
        .withEsMpvId(props.routeSpecifiedMpvId);
    const [url, setUrl] = useState(props.cta.targetUrl || defaultCtaUrl.url);
    const [selectedProductOption, setSelectedProductOption] = useState<Record<string, string>>(
        getPreselectedOptions(props.productOptions)
    );

    const { price, currency, pivotPricing, loading } = useProductOptionsPricing({
        productKey: props.servicePrd,
        pimMerchantVersion: props.serviceVersion,
        selectedProductOptions: selectedProductOption,
        productOptions: props.productOptions,
    });

    // Only set a dev url once on render
    const didRun = useRef(false);
    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;

        if (props.environment === 'development') {
            try {
                const parsedUrl = new URL(url);
                setUrl(parsedUrl.pathname + parsedUrl.search + parsedUrl.hash);
            } catch {
                // Fallback if url is already relative or malformed
                setUrl(url.replace(/^https?:\/\/[^/]+/, ''));
            }
        }
    }, [props.environment, url]);

    // Set the url when selected product options change
    useEffect(() => {
        if (Object.keys(selectedProductOption).length === 0) {
            return;
        }

        const productOptions = new ServiceProductOptions(
            Object.entries(selectedProductOption).map(([key, value]) => ({
                __typename: 'KeyValue',
                key,
                value,
            }))
        );
        const encodedProductOptions = productOptions.asEncodedURIComponent;

        const newUrl = new PrimaryCTAUrlBuilder(getRequestBriefCreateUrl())
            .withPrintMpvId(props.printMpvId)
            .withEsMpvId(props.routeSpecifiedMpvId)
            .withServiceSelectedOptions(encodedProductOptions)
            .withWorkId(props.workId);

        setUrl(newUrl.url);
    }, [
        props.printMpvId,
        props.environment,
        props.routeSpecifiedMpvId,
        props.workId,
        props.cta.targetUrl,
        selectedProductOption,
        defaultCtaUrl,
    ]);

    return (
        <BoundedContent>
            <GridContainer py={3}>
                <Row sticky>
                    <Column span={7} pl={0}>
                        <ThumbnailsHero accessibleTextForDots={accessibleTextForDots}>
                            <ThumbnailsHeroImagesSlidesContainer>
                                {props.heroImages?.map((image, i) => (
                                    <CarouselSlide
                                        key={`carouselimage1-${i}`}
                                        backgroundColor={'standard'}
                                    >
                                        <FluidImage
                                            srcSet={buildSrcset(image.cloudinaryImage)}
                                            alt={image.altTextOverride || ''}
                                            role="presentation"
                                            style={{
                                                height: 'auto',
                                                width: '100%',
                                                borderRadius: tokens.SwanBaseBorderRadius400,
                                            }}
                                        />
                                    </CarouselSlide>
                                ))}
                            </ThumbnailsHeroImagesSlidesContainer>
                            {props.heroImages?.length >= 2 && (
                                <ThumbnailsHeroThumbnailsSlidesContainer>
                                    {props.heroImages?.map((image, i) => (
                                        <CarouselSlide key={`carouselimage2-${i}`}>
                                            <FluidImage
                                                src={
                                                    image.cloudinaryImage.thumbnailCloudinaryUrl ||
                                                    ''
                                                }
                                                alt=""
                                                role="presentation"
                                            />
                                        </CarouselSlide>
                                    ))}
                                </ThumbnailsHeroThumbnailsSlidesContainer>
                            )}
                        </ThumbnailsHero>
                    </Column>
                    <Column span={5} sticky>
                        <FlexBox
                            flexDirection="column"
                            gap={'between-subsections'}
                            mt={7}
                            ml={{ xs: 0, sm: 4 }}
                        >
                            <Box>
                                <Typography>{props.sectionTitle}</Typography>
                                <Box mt={3}>
                                    <H1 mb={0} fontSkin="title-headline">
                                        {props.name}
                                    </H1>
                                </Box>
                                <Box mt={4}>
                                    <Pricing>
                                        <ListPrice fontSkin="title-subsection">
                                            {loading ? (
                                                <SkeletonText
                                                    style={{ width: '30%' }}
                                                    data-testid="skeleton-text"
                                                >
                                                    <SkeletonTextLine width="narrow"></SkeletonTextLine>
                                                </SkeletonText>
                                            ) : (
                                                <PriceFromFloat
                                                    amount={price}
                                                    currency={currency}
                                                />
                                            )}
                                        </ListPrice>
                                    </Pricing>
                                </Box>
                            </Box>
                            <Box>
                                <Typography>{props.description}</Typography>
                            </Box>
                            {inclusions()}
                            <ProductOptions
                                productOptions={props.productOptions}
                                selectedProductOptions={selectedProductOption || []}
                                setSelectedProductOptions={setSelectedProductOption}
                                pivotPricing={pivotPricing}
                                currency={currency}
                                loading={loading}
                            />
                            {price === 0.0 ? (
                                <Button disabled skin="primary" width="full-width">
                                    {loading ? props.cta.text : __('Not available')}
                                </Button>
                            ) : (
                                <Button
                                    skin="primary"
                                    width="full-width"
                                    render={(p) => (
                                        <Link
                                            to={url}
                                            className={p.className}
                                            data-testid="Primary-CTA"
                                            onClick={() => {
                                                sendNavigationEvent({
                                                    ...clickEventData(url),
                                                    ...pageData,
                                                });
                                            }}
                                        >
                                            {p.children}
                                        </Link>
                                    )}
                                >
                                    {props.cta.text}
                                </Button>
                            )}
                        </FlexBox>
                    </Column>
                </Row>
            </GridContainer>
        </BoundedContent>
    );
}
